import React, {useContext, useState, useEffect} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoon, faSun, faTrash, faWandMagic } from '@fortawesome/free-solid-svg-icons';
import SettingRow from '../Subcomponents/SettingRow/SettingRow.tsx';
import ThemeButton from '../Subcomponents/ThemeButton/ThemeButton.tsx';
import ToggleSwitch from '../Subcomponents/ToggleSwitch/ToggleSwitch.tsx';
import InfoButton from "../Subcomponents/InfoButton/InfoButton.tsx";
import {useAppStore} from "../../../../../GlobalStore/GlobalStore.ts";
import SectionSeparator from "../Subcomponents/SectionSeparator/SectionSeparator.tsx";
import SpeechCommandsSection from "../Subcomponents/SpeechCommandsSection/SpeechCommandsSection.tsx";
import SaveButton from "../Subcomponents/SaveButton/SaveButton.tsx";
import {GlobalContext} from "../../../../../App.tsx";
import {UnsavedChangesContext, TabStateContext} from "../../Settings.tsx";
import {writeSettings} from "../../../../../Utils/db/users/userSettings/writeSettings.ts";
import {isIde} from "../../../../../Utils/misc/envNameUtils.ts";

interface GeneralProps {
    setShowConfirmation: (show: boolean) => void;
}

const General: React.FC<GeneralProps> = ({ setShowConfirmation }) => {
    const { resetStateHelper, messagesSequenceRef } = useContext(GlobalContext);
    const { setTabHasUnsavedChanges } = useContext(UnsavedChangesContext);
    const { getTabState, setTabState } = useContext(TabStateContext);
    const settingsObj = useAppStore((state: any) => state.settingsObj);
    const setSettingsObj = useAppStore((state: any) => state.setSettingsObj);
    const {
        theme,
        autoTheme,
        sendMessagePhrase,
        stopOutputPhrase,
        stopRecordingPhrase,
        primaryLanguage,
        secondaryLanguage,
        isIncognito
    } = settingsObj;
    const environmentObj = useAppStore((state: any) => state.environmentObj);
    const userObj = useAppStore((state: any) => state.userObj);
    const setDynamicStateObj = useAppStore((state: any) => state.setDynamicStateObj);

    // Get persistent tab state or initialize with current values
    const persistentState = getTabState('General');
    const initialSettings = persistentState.localSettings || {
        theme,
        autoTheme,
        sendMessagePhrase,
        stopOutputPhrase,
        stopRecordingPhrase,
        primaryLanguage,
        secondaryLanguage,
        isIncognito
    };

    // Local state for tracking unsaved changes
    const [localSettings, setLocalSettings] = useState(initialSettings);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(persistentState.hasUnsavedChanges || false);

    // Update local settings when global settings change (only when they actually change from external source)
    useEffect(() => {
        const newSettings = {
            theme,
            autoTheme,
            sendMessagePhrase,
            stopOutputPhrase,
            stopRecordingPhrase,
            primaryLanguage,
            secondaryLanguage,
            isIncognito
        };

        // Only update if this is an external change (not from our local updates)
        if (!hasUnsavedChanges) {
            setLocalSettings(newSettings);
        }
    }, [theme, autoTheme, sendMessagePhrase, stopOutputPhrase, stopRecordingPhrase, primaryLanguage, secondaryLanguage, isIncognito, hasUnsavedChanges]);

    // Report unsaved changes status to parent Settings component
    useEffect(() => {
        setTabHasUnsavedChanges('General', hasUnsavedChanges);
    }, [hasUnsavedChanges, setTabHasUnsavedChanges]);

    // Persist state changes to TabStateContext
    useEffect(() => {
        setTabState('General', {
            localSettings,
            hasUnsavedChanges
        });
    }, [localSettings, hasUnsavedChanges, setTabState]);

    // Update local state and track changes
    const updateLocalSetting = (key: string, value: any) => {
        const newLocalSettings = { ...localSettings, [key]: value };
        setLocalSettings(newLocalSettings);

        // Check if the new settings are different from original global settings
        const originalSettings = {
            theme,
            autoTheme,
            sendMessagePhrase,
            stopOutputPhrase,
            stopRecordingPhrase,
            primaryLanguage,
            secondaryLanguage,
            isIncognito
        };

        const hasActualChanges = Object.keys(newLocalSettings).some(settingKey => {
            return newLocalSettings[settingKey] !== originalSettings[settingKey as keyof typeof originalSettings];
        });

        setHasUnsavedChanges(hasActualChanges);
    };

    // Theme change handler - updates both theme and autoTheme at once
    const handleThemeChange = (themeValue: string, autoThemeValue: boolean) => {
        const newLocalSettings = {
            ...localSettings,
            theme: themeValue,
            autoTheme: autoThemeValue
        };
        setLocalSettings(newLocalSettings);

        // Check if the new settings are different from original global settings
        const originalSettings = {
            theme,
            autoTheme,
            sendMessagePhrase,
            stopOutputPhrase,
            stopRecordingPhrase,
            primaryLanguage,
            secondaryLanguage,
            isIncognito
        };

        const hasActualChanges = Object.keys(newLocalSettings).some(settingKey => {
            return newLocalSettings[settingKey] !== originalSettings[settingKey as keyof typeof originalSettings];
        });

        setHasUnsavedChanges(hasActualChanges);
    };

    // Save settings to persistent storage
    const handleSave = async () => {
        try {
            // Clear unsaved changes flag immediately to prevent confirmation popup if user closes quickly
            setHasUnsavedChanges(false);

            // Update global state
            setSettingsObj(localSettings);

            // Handle incognito mode special logic if it changed
            if (localSettings.isIncognito !== isIncognito) {
                setDynamicStateObj({ conversationId: null });
                if (!localSettings.isIncognito && messagesSequenceRef.current.length !== 0) {
                    setDynamicStateObj({ visiblePopup: 'askToSave' });
                } else {
                    resetStateHelper();
                }
            }

            // Save to persistent storage
            const settings = {
                theme: localSettings.theme,
                autoTheme: localSettings.autoTheme,
                sendMessagePhrase: localSettings.sendMessagePhrase,
                stopOutputPhrase: localSettings.stopOutputPhrase,
                stopRecordingPhrase: localSettings.stopRecordingPhrase,
                primaryLanguage: localSettings.primaryLanguage,
                secondaryLanguage: localSettings.secondaryLanguage,
                incognitoMode: localSettings.isIncognito
            };
            await writeSettings(userObj.user, settings, environmentObj.environment);
        } catch (error) {
            console.error('Failed to save settings:', error);
            // If save fails, restore the unsaved changes flag
            setHasUnsavedChanges(true);
        }
    };

    const handleIncognitoToggle = () => {
        updateLocalSetting('isIncognito', !localSettings.isIncognito);
    };

    return (
        <div className="dark:text-neutral-300 space-y-4 flex flex-col h-full">
            <div className="flex-1 space-y-4">
                <SectionSeparator>
                    <SettingRow
                        label="Theme"
                        content={
                            <div className={`flex space-x-2`}>
                                <ThemeButton
                                    theme="auto"
                                    icon={faWandMagic}
                                    isSelected={localSettings.autoTheme}
                                    onClick={() => handleThemeChange('auto', true)}
                                />
                                <ThemeButton
                                    theme="light"
                                    icon={faSun}
                                    isSelected={localSettings.theme === 'light' && !localSettings.autoTheme}
                                    onClick={() => handleThemeChange('light', false)}
                                />
                                <ThemeButton
                                    theme="dark"
                                    icon={faMoon}
                                    isSelected={localSettings.theme === 'dark' && !localSettings.autoTheme}
                                    onClick={() => handleThemeChange('dark', false)}
                                />
                            </div>
                        }
                    />
                </SectionSeparator>

                <SectionSeparator>
                    <SettingRow
                        label={
                            <div className="flex items-center space-x-1">
                                <p>Incognito Mode</p>
                                <InfoButton
                                    tooltipId="incognito-mode-tooltip"
                                    tooltipContent="When enabled, conversations are not saved to your chat history."
                                />
                            </div>
                        }
                        content={
                            <ToggleSwitch
                                enabled={localSettings.isIncognito}
                                onChange={handleIncognitoToggle}
                            />
                        }
                    />
                </SectionSeparator>
                {isIde(environmentObj.environment) && (
                    <SectionSeparator>
                        <SpeechCommandsSection
                            sendMessagePhrase={localSettings.sendMessagePhrase}
                            stopOutputPhrase={localSettings.stopOutputPhrase}
                            stopRecordingPhrase={localSettings.stopRecordingPhrase}
                            primaryLanguage={localSettings.primaryLanguage}
                            secondaryLanguage={localSettings.secondaryLanguage}
                            onUpdateSettings={(settings) => {
                                Object.keys(settings).forEach(key => {
                                    updateLocalSetting(key, settings[key]);
                                });
                            }}
                        />
                    </SectionSeparator>
                )}
            </div>

            <SaveButton
                hasUnsavedChanges={hasUnsavedChanges}
                onSave={handleSave}
            />

            <div className="pt-4 pb-2 flex justify-center">
                <button
                    className="py-2 px-4 bg-red-600 font-medium text-white rounded-md hover:bg-red-700 transition-colors duration-200 flex items-center justify-center"
                    onClick={() => setShowConfirmation(true)}
                >
                    <FontAwesomeIcon icon={faTrash} className="mr-2"/>
                    Clear Chat History
                </button>
            </div>
        </div>
    );
};

export default General;